import { useReducer } from 'react';

/**
 * Returns a function that toggles a boolean React component state value.
 *
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setter Setter function for the state value
 */
export const toggleState = setter => () => setter(e => !e);

/**
 * Retrieves and stores a React component state value in local storage.
 *
 * @template T
 * @param {string} key Key name of the value in local storage
 * @param {T} initialValue Initial state value to use if no value is stored
 * @return {[T, React.Dispatch<React.SetStateAction<T>>]}
 */
export const useStateWithLocalStorage = (key, initialValue = null) => {
  /** @type {React.Reducer<T, React.SetStateAction<T>>} */
  const reducer = (previousValue, action) => {
    /** @type {T} */
    const newValue =
      typeof action === 'function' ? action(previousValue) : action;

    localStorage.setItem(key, JSON.stringify(newValue));

    return newValue;
  };

  /** @type {T} */
  const storedValue = JSON.parse(localStorage.getItem(key));

  // Check for a value in local storage, if not found then return initial value
  const [value, dispatch] = useReducer(
    reducer,
    storedValue === null ? initialValue : storedValue,
  );

  return [value, dispatch];
};
